import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { MapPin, Phone, ExternalLink } from "lucide-react";
import { useBrand, useSiteSection } from "@/hooks/useSiteContent";

export const Route = createFileRoute("/lojas")({
  head: () => ({
    meta: [
      { title: "Lojas Contato Internet — Endereços e atendimento presencial" },
      {
        name: "description",
        content:
          "Encontre a loja Contato Internet mais próxima: endereços, bairros e telefone para atendimento presencial no Sul de Santa Catarina.",
      },
      { property: "og:title", content: "Lojas Contato Internet" },
      { property: "og:description", content: "Endereços e atendimento presencial da Contato." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: StoresPage,
});

function StoresPage() {
  const stores = useSiteSection("stores");
  const brand = useBrand();
  const items = (stores.items ?? []).filter((s) => s.name);
  const [active, setActive] = useState(0);
  const selected = items[active] ?? items[0];
  const mapQuery = selected
    ? encodeURIComponent(
        [selected.name, selected.address, selected.district, selected.zip]
          .filter(Boolean)
          .join(", "),
      )
    : "";

  return (
    <>
      <section className="bg-[linear-gradient(110deg,var(--brand-sky-deep),var(--brand-deep))] py-14 text-brand-deep-foreground sm:py-20">
        <div className="mx-auto max-w-7xl px-5">
          <p className="text-[0.7rem] font-extrabold uppercase tracking-[0.2em] text-brand-yellow">
            Atendimento presencial
          </p>
          <h1 className="mt-2 text-3xl font-extrabold uppercase leading-tight sm:text-4xl">
            {stores.title}
          </h1>
          <p className="mt-3 max-w-2xl text-sm text-brand-sky sm:text-base">{stores.subtitle}</p>
        </div>
      </section>

      <section className="bg-background py-12 sm:py-16">
        <div className="mx-auto grid max-w-7xl gap-6 px-5 sm:grid-cols-2 lg:grid-cols-3">
          {items.map((store, i) => (
            <article
              key={store.name}
              onClick={() => setActive(i)}
              className={`flex cursor-pointer flex-col rounded-3xl border bg-card p-6 transition hover:-translate-y-1 hover:shadow-lg ${
                i === active ? "border-primary shadow-md" : "border-border"
              }`}
            >
              <span className="grid size-11 place-items-center rounded-2xl bg-secondary text-primary">
                <MapPin className="size-5" strokeWidth={2.4} />
              </span>
              <h2 className="mt-4 text-base font-extrabold uppercase text-brand-deep">
                {store.name}
              </h2>
              <p className="mt-2 text-sm text-muted-foreground">{store.address}</p>
              <p className="text-sm text-muted-foreground">{store.district}</p>
              {store.zip ? <p className="text-sm text-muted-foreground">CEP {store.zip}</p> : null}
              <p className="mt-3 inline-flex items-center gap-1.5 text-sm font-bold text-brand-sky">
                <Phone className="size-4" />
                {brand.phoneDisplay}
              </p>
              <div className="mt-auto flex items-center gap-4 pt-4">
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setActive(i);
                    document
                      .getElementById("mapa-lojas")
                      ?.scrollIntoView({ behavior: "smooth", block: "center" });
                  }}
                  className="inline-flex items-center gap-1.5 text-sm font-extrabold text-primary hover:underline"
                >
                  Ver no mapa
                  <MapPin className="size-4" />
                </button>
                {store.mapUrl ? (
                  <a
                    href={store.mapUrl}
                    target="_blank"
                    rel="noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="inline-flex items-center gap-1.5 text-sm font-bold text-muted-foreground hover:underline"
                  >
                    Rotas
                    <ExternalLink className="size-4" />
                  </a>
                ) : null}
              </div>
            </article>
          ))}
        </div>

        {selected ? (
          <div id="mapa-lojas" className="mx-auto mt-10 max-w-7xl px-5">
            <div className="overflow-hidden rounded-3xl border border-border bg-card">
              <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border px-5 py-4">
                <p className="text-sm font-extrabold uppercase text-brand-deep">{selected.name}</p>
                <p className="text-xs text-muted-foreground">
                  {[selected.address, selected.district].filter(Boolean).join(" — ")}
                </p>
              </div>
              <iframe
                key={mapQuery}
                title={`Mapa da loja ${selected.name}`}
                src={`https://www.google.com/maps?q=${mapQuery}&output=embed&hl=pt-BR&z=16`}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                className="h-[320px] w-full border-0 sm:h-[440px]"
              />
            </div>
          </div>
        ) : null}
      </section>
    </>
  );
}
